import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";

const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Project = lazy(() => import("./pages/Project"));
const Calendar = lazy(() => import("./pages/Calendar"));
const FlowDiagram = lazy(() => import("./pages/FlowDiagram"));
const TaskBoardPage = lazy(() => import("./pages/TaskBoardPage"));
const WorkspaceSettings = lazy(() => import("./pages/WorkspaceSettings"));
const AccountSettings = lazy(() => import("./pages/AccountSettings"));
const Trash = lazy(() => import("./pages/Trash"));

function PageLoader() {
  return <div className="app-page-loader" role="status" aria-live="polite"><span className="app-spinner" /> Cargando…</div>;
}

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, resetKey: props.resetKey };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  static getDerivedStateFromProps(props, state) {
    if (props.resetKey !== state.resetKey) {
      return { error: null, resetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error, info) {
    console.error("Error de interfaz", error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="app-crash">
          <h2>No se pudo mostrar esta vista</h2>
          <p>La aplicación encontró un error inesperado. Tus datos no se han eliminado.</p>
          <button
            className="btn btn-primary"
            onClick={() => window.location.replace(`/?fresh=${Date.now()}`)}
          >
            Volver a Mi espacio de trabajo
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return (
    <Layout>
      <AppErrorBoundary resetKey={`${location.key}:${location.pathname}`}>
        {children}
      </AppErrorBoundary>
    </Layout>
  );
}

export default function App() {
  const { user } = useAuth();
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
        <Route path="/register" element={user ? <Navigate to="/" replace /> : <Register />} />
        <Route path="/" element={<Protected><Dashboard /></Protected>} />
        <Route path="/calendario" element={<Protected><Calendar /></Protected>} />
        <Route path="/configuracion/espacio" element={<Protected><WorkspaceSettings /></Protected>} />
        <Route path="/configuracion/cuenta" element={<Protected><AccountSettings /></Protected>} />
        <Route path="/papelera" element={<Protected><Trash /></Protected>} />
        <Route path="/pizarra/:boardRef" element={<Protected><TaskBoardPage /></Protected>} />
        <Route path="/gantt/:id" element={<Protected><Project /></Protected>} />
        <Route path="/projects/:id" element={<Protected><Project /></Protected>} />
        <Route path="/diagramas/:publicId" element={<Protected><FlowDiagram /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
